import { html, nothing } from 'lit';
import { fenceRendererFor } from './work-card';
const FENCE = /^\s*(`{3,}|~{3,})\s*([\w+-]*)\s*$/;
const HEAD = /^(#{1,6})\s+(.*)$/;
const BULLET = /^\s*[-*+]\s+(.*)$/;
const ORDERED = /^\s*\d+[.)]\s+(.*)$/;
const RULE = /^\s*(?:---+|\*\*\*+|___+)\s*$/;
const QUOTE = /^\s*>\s?(.*)$/;
// A table row is a |-delimited line; the row AFTER the header must be the separator (|---|:--:|…).
// Both checked, because a lone pipe-bearing line is prose about shell pipes far more often than a
// one-row table, and rendering prose as a grid is worse than rendering a table as prose.
const TROW = /^\s*\|(.+)\|\s*$/;
const TSEP = /^\s*\|(?:\s*:?-+:?\s*\|)+\s*$/;
/** One table line → its cells. A `\|` inside a cell survives as a literal pipe. */
function tableCells(line) {
    return line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split(/(?<!\\)\|/)
        .map((c) => c.replace(/\\\|/g, '|').trim());
}
/** Split into blocks. Fenced code wins over everything — its contents are never parsed. */
export function blocks(src) {
    const lines = src.split('\n');
    const out = [];
    let para = [];
    let quote = [];
    const flushPara = () => {
        if (para.length)
            out.push({ kind: 'p', text: para.join('\n').trim() });
        para = [];
    };
    const flushQuote = () => {
        if (quote.length)
            out.push({ kind: 'quote', text: quote.join('\n').trim() });
        quote = [];
    };
    const flush = () => {
        flushPara();
        flushQuote();
    };
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const fence = FENCE.exec(line);
        if (fence) {
            flush();
            const marker = fence[1];
            const body = [];
            i++;
            // An UNCLOSED fence still renders as code. Mid-stream that is the normal state: the model
            // has written ```python and forty lines and has not reached the closing fence yet, and
            // showing those lines as prose until it does would make the block flicker into existence.
            for (; i < lines.length; i++) {
                const end = FENCE.exec(lines[i]);
                if (end && end[1].startsWith(marker[0]) && end[1].length >= marker.length && !end[2])
                    break;
                body.push(lines[i]);
            }
            out.push({ kind: 'code', lang: fence[2] ?? '', text: body.join('\n') });
            continue;
        }
        if (!line.trim()) {
            flush();
            continue;
        }
        const q = QUOTE.exec(line);
        if (q) {
            flushPara();
            quote.push(q[1] ?? '');
            continue;
        }
        flushQuote();
        if (RULE.test(line)) {
            flush();
            out.push({ kind: 'hr' });
            continue;
        }
        const h = HEAD.exec(line);
        if (h) {
            flush();
            out.push({ kind: 'h', level: h[1].length, text: h[2].trim() });
            continue;
        }
        const bullet = BULLET.exec(line);
        const ordered = bullet ? null : ORDERED.exec(line);
        if (bullet || ordered) {
            flush();
            const isOrdered = !!ordered;
            const items = [(bullet ?? ordered)[1]];
            // Consume the run, and only the run: a list ends at the first line that is not an item of
            // the same kind, so a bulleted list directly under a numbered one stays two lists.
            while (i + 1 < lines.length) {
                const next = lines[i + 1];
                const nb = BULLET.exec(next);
                const no = ORDERED.exec(next);
                if (isOrdered ? !no : !nb)
                    break;
                items.push((nb ?? no)[1]);
                i++;
            }
            out.push({ kind: 'list', ordered: isOrdered, items });
            continue;
        }
        /*
         * TABLES, which this renderer silently lacked. A model that answers "which symbols are locked"
         * with a GFM table — which is what a model does — rendered as one paragraph of literal pipes.
         * Recognised only as header + separator + rows, exactly the GFM shape; a pipe-bearing line
         * whose successor is not a separator stays prose, so talk ABOUT pipes never becomes a grid.
         */
        if (TROW.test(line) && i + 1 < lines.length && TSEP.test(lines[i + 1])) {
            flush();
            const head = tableCells(line);
            const rows = [];
            i++; // the separator row renders as nothing
            while (i + 1 < lines.length && TROW.test(lines[i + 1]) && !TSEP.test(lines[i + 1])) {
                i++;
                // Ragged rows are padded/truncated to the header, as GFM does — a model mid-stream often
                // has written half a row, and a cell count that jumps per frame would reflow the grid.
                const cells = tableCells(lines[i]);
                rows.push(head.map((_, c) => cells[c] ?? ''));
            }
            out.push({ kind: 'table', head, rows });
            continue;
        }
        para.push(line);
    }
    flush();
    return out;
}
const CODE = /`([^`]+)`/;
const LINK = /\[([^\]]+)\]\(([^)\s]+)\)/;
/*
 * AN UNDERSCORE INSIDE A WORD IS NOT EMPHASIS — CommonMark says so, and a symbol path is why.
 *
 * `_([^_]+)_` matches anything between any two underscores, so a sentence naming two PLC symbols —
 * "the FastChanging.Sine_00 series … and the FastChanging.Sine_06 series" — was rendered with
 * everything between them italicised and both underscores eaten: `FastChanging.Sine00`, a path that
 * does not exist, in an agent's own audit transcript. The same defect ate a deliverable key
 * (`a_CPU_Usage` → `aCPUUsage`) and was answered then by hiding the envelope; it was never the
 * envelope, it was this, and it reaches every sentence a model writes about a symbol.
 *
 * CommonMark distinguishes the two delimiters exactly here: `*` may emphasise intra-word, `_` may
 * not. The guards are the practical half of its flanking rule — an opening `_` may not follow a
 * word character, a closing `_` may not precede one — which is what makes `snake_case_names` safe
 * while leaving deliberate _emphasis_ alone. Asterisks keep the permissive form on purpose.
 *
 * `\w` and not `[A-Za-z0-9]`, because the underscore has to count as a word character itself:
 * without it the guard reads the SECOND `_` of a `__` pair as boundary-clean, and `DEBUG__LEVEL__MAX`
 * comes out with `LEVEL` italicised — the identifier mangled by the very rule written to protect it.
 */
const BOLD = /\*\*([^*]+)\*\*|(?<!\w)__([^_]+)__(?!\w)/;
const ITALIC = /\*([^*]+)\*|(?<!\w)_([^_]+)_(?!\w)/;
const STRIKE = /~~([^~]+)~~/;
/**
 * Inline spans, innermost-first by earliest match. Inline code is matched BEFORE emphasis so
 * `**` inside a code span stays literal — which is the case that matters, because models write
 * code spans containing exactly those characters.
 */
export function inline(text) {
    if (!text)
        return [];
    const out = [];
    let rest = text;
    while (rest) {
        const cands = [];
        const push = (re, make) => {
            const m = re.exec(rest);
            if (m)
                cands.push([m.index, m, make]);
        };
        push(CODE, (m) => html `<code class="md-code">${m[1]}</code>`);
        push(LINK, (m) => renderLink(m[1], m[2]));
        push(BOLD, (m) => html `<strong>${inline(m[1] ?? m[2] ?? '')}</strong>`);
        push(STRIKE, (m) => html `<s>${inline(m[1])}</s>`);
        push(ITALIC, (m) => html `<em>${inline(m[1] ?? m[2] ?? '')}</em>`);
        if (!cands.length) {
            out.push(rest);
            break;
        }
        cands.sort((a, b) => a[0] - b[0]);
        const [at, m, make] = cands[0];
        if (at > 0)
            out.push(rest.slice(0, at));
        out.push(make(m));
        rest = rest.slice(at + m[0].length);
    }
    return out;
}
/**
 * IN-APP PATHS NAVIGATE; everything else opens away.
 *
 * An assistant that can say "it is on /gateways/abc/controllers/def" should be able to make that a
 * link, and a link INSIDE the product must not open a second copy of the product in a new tab. So a
 * path — `/usage`, or an absolute URL on this same origin — becomes an ordinary in-app navigation:
 * the identical `pushState` + `popstate` that `platform.navigate` performs, because a link and that
 * tool are the same act expressed two ways, and two mechanisms for one act drift.
 *
 * The difference between them is CONSENT, which is why both exist. The tool moves the person; the
 * link offers to. Most of the time offering is the better manners.
 *
 * Modified clicks are left alone — ctrl/cmd/middle-click means "open a copy", and a handler that
 * swallowed that would break the one browser convention people rely on without being told.
 *
 * Everything else is unchanged: another origin opens in a new tab, and a `javascript:` or `data:`
 * href — prompt injection or accident — is rendered as text with its target visible, so nothing is
 * hidden from the reader.
 */
function inAppPath(href) {
    const h = href.trim();
    // A protocol-relative `//evil.com` is NOT a path, however much it looks like one.
    if (h.startsWith('/') && !h.startsWith('//'))
        return h;
    if (typeof location === 'undefined')
        return '';
    try {
        const u = new URL(h, location.origin);
        return u.origin === location.origin ? u.pathname + u.search + u.hash : '';
    }
    catch {
        return '';
    }
}
function navigateInApp(e, path) {
    // Let the browser do what the person asked for: a new tab, a new window, a download.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
        return;
    e.preventDefault();
    history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
}
function renderLink(label, href) {
    const path = inAppPath(href);
    if (path) {
        // A real href, not a button dressed as a link: hover shows where it goes, and
        // cmd-click still opens it in a tab, because it is genuinely that address.
        return html `<a class="md-link" href=${path} @click=${(e) => navigateInApp(e, path)}>${inline(label)}</a>`;
    }
    const safe = /^(https?:|mailto:)/i.test(href.trim());
    if (!safe)
        return html `<span>${label} (${href})</span>`;
    return html `<a class="md-link" href=${href} target="_blank" rel="noopener noreferrer">${inline(label)}</a>`;
}
/** Render markdown as templates. `onCopyCode` gets the raw source of a block, for a copy button. */
export function renderMarkdown(src, onCopyCode, 
/** The turn's handlers, for a fence that renders as a work card and needs its view toggle. */
fenceHandlers) {
    if (!src)
        return nothing;
    return html `<div class="md">
    ${blocks(src).map((b) => {
        switch (b.kind) {
            case 'code': {
                /*
                 * A FENCE THE PRODUCT KNOWS HOW TO DRAW IS DRAWN THAT WAY.
                 *
                 * An agent asked for a statechart in prose returns it as a ```scxml block, and the same
                 * document written through `models.write` gets a work card with a live preview. Two
                 * renderings of one thing, and the reader has no idea why one of them is a wall of
                 * escaped XML — reported exactly that way.
                 *
                 * Registered per language, by whoever owns it, for the same reason the work cards are:
                 * base-ui does not know what a statechart is, and the app that does can say so once and
                 * have both paths agree.
                 */
                const fenced = fenceRendererFor(b.lang);
                if (fenced)
                    return fenced(b.text, fenceHandlers);
                return html `<div class="md-fence">
            <div class="md-fence-head">
              <span class="md-lang">${b.lang || 'text'}</span>
              ${onCopyCode ? html `<button class="md-copy" @click=${() => onCopyCode(b.text)} title="Copy">Copy</button>` : nothing}
            </div>
            <pre class="md-pre"><code>${b.text}</code></pre>
          </div>`;
            }
            case 'h':
                return html `<div class="md-h md-h${Math.min(b.level, 4)}">${inline(b.text)}</div>`;
            case 'list':
                return b.ordered
                    ? html `<ol class="md-list">
                ${b.items.map((x) => html `<li>${inline(x)}</li>`)}
              </ol>`
                    : html `<ul class="md-list">
                ${b.items.map((x) => html `<li>${inline(x)}</li>`)}
              </ul>`;
            case 'quote':
                return html `<blockquote class="md-quote">${inline(b.text)}</blockquote>`;
            case 'table':
                return html `<div class="md-table-wrap">
            <table class="md-table">
              <thead>
                <tr>
                  ${b.head.map((c) => html `<th>${inline(c)}</th>`)}
                </tr>
              </thead>
              <tbody>
                ${b.rows.map((r) => html `<tr>
                      ${r.map((c) => html `<td>${inline(c)}</td>`)}
                    </tr>`)}
              </tbody>
            </table>
          </div>`;
            case 'hr':
                return html `<div class="md-hr"></div>`;
            default:
                return html `<p class="md-p">${inline(b.text)}</p>`;
        }
    })}
  </div>`;
}
//# sourceMappingURL=markdown.js.map